import { useEffect, type RefObject } from 'react';

import type { InteractionMode } from '../components/interactions-overlay/types.js';
import { shouldConsumeWheelEvent } from '../components/interactions-overlay/utils/interaction-mode.js';

/**
 *
 * @param event - The event to check if it is a WheelEvent
 * @returns True if the event is a WheelEvent, false otherwise
 */

const isWheelEvent = (event: Event): event is WheelEvent =>
  'metaKey' in event || 'ctrlKey' in event;

/**
 * A custom React hook that prevents the default scrolling behavior of the page
 * when the Command (Mac), Control (Windows), or Option/Alt key is pressed while
 * using the mouse wheel.
 *
 * @param targetRef - An optional React ref object pointing to a target HTMLDivElement
 * or SVGRectElement. If not provided, the event listener will be attached to the
 * document.
 * @param mode - The active interaction mode. When `pan` or `zoom` is active, wheel
 * events are consumed regardless of modifier keys. Defaults to `explore`, where only
 * a temporary modifier (Alt / Cmd / Ctrl) consumes the event.
 */
export function usePreventScrollOnWheel(
  targetRef?: RefObject<HTMLDivElement | null>,
  mode: InteractionMode = 'explore',
) {
  useEffect(() => {
    const handleWheel = (event: Event) => {
      if (isWheelEvent(event) && shouldConsumeWheelEvent(event, mode)) {
        event.preventDefault();
      }
    };

    const targetRefCurrent = targetRef?.current ?? document;

    targetRefCurrent.addEventListener('wheel', handleWheel, {
      passive: false,
    });

    return () => {
      targetRefCurrent.removeEventListener('wheel', handleWheel);
    };
  }, [targetRef, mode]);
}
